import { useMutation } from "@tanstack/react-query";
import { useState } from "react";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";

import { commands, DistributionHalt, type Phase } from "./api";
import { ActionError, GroupLabel } from "./ui";

// The single advance control for every surface that moves an opportunity a phase forward.
//
// C2 gates a transition on a per-transition checklist (manifests/checklists-v1.yaml) and only
// tells the client which items are outstanding by REFUSING the first attempt and naming them in
// the halt. So the first press is a question, not a failure: when the halt is NEEDS_CHECKLIST we
// replace the button and the red error with the checklist itself, rather than showing an alarming
// strip that repeats the same two item names the checkboxes already list (bug c1754cd8 round 2).
//
// The items are hardcoded nowhere on this side on purpose. Mirroring C2's manifest here is how
// the sort=rank, fact-code and gaps/needs drifts all happened.
export function AdvanceControl({ opportunityId, next, label, size = "default", variant = "default", onAdvanced }: {
  opportunityId: string;
  next: Phase;
  label: string;
  size?: "default" | "sm";
  variant?: "default" | "outline";
  onAdvanced: () => void;
}) {
  const [confirmed, setConfirmed] = useState<Record<string, boolean>>({});
  const mutation = useMutation({
    mutationFn: (checklist: Record<string, boolean>) => commands.advancePhase({ opportunity: opportunityId, to_phase: next, checklist }),
    onSuccess: () => { setConfirmed({}); onAdvanced(); },
  });
  const outstanding = mutation.error instanceof DistributionHalt && mutation.error.halt === "NEEDS_CHECKLIST" ? mutation.error.needs : [];
  const ready = outstanding.length > 0 && outstanding.every((item) => confirmed[item]);

  if (outstanding.length > 0) return <div className="space-y-2 rounded-lg bg-ks-grey-5 p-2.5">
    <GroupLabel>Confirm before advancing</GroupLabel>
    {outstanding.map((item) => <div key={item} className="flex items-center gap-2">
      <Checkbox id={`advance-${opportunityId}-${item}`} checked={Boolean(confirmed[item])} onCheckedChange={(value) => setConfirmed({ ...confirmed, [item]: value === true })} />
      <Label htmlFor={`advance-${opportunityId}-${item}`} className="text-[12.5px] font-normal">{item.replaceAll("_", " ")}</Label>
    </div>)}
    <Button size="sm" disabled={!ready || mutation.isPending} onClick={() => mutation.mutate(confirmed)}>{label}</Button>
  </div>;

  return <div className="space-y-2">
    <Button size={size} variant={variant} disabled={mutation.isPending} onClick={() => mutation.mutate(confirmed)}>{label}</Button>
    {mutation.error && <ActionError error={mutation.error} />}
  </div>;
}
